<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import type { TwoRowBentoGridProps } from "./bento.interfaces";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Card from "./two-row-bento-grid/card/Card.vue";

const props = defineProps<{
    block: PageBlock<TwoRowBentoGridProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing class="max-w-2xl lg:max-w-7xl mx-auto">
            <div class="flex flex-col gap-4">
                <!-- Header -->
                <div class="flex flex-col gap-2 max-w-2xl">
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <p :style="{ color: styleValues.brand }" class="font-semibold">
                            {{ dataValues.tag.data.text }}
                        </p>
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :text="dataValues.title.data.text"
                            :textColor="styleValues.text"
                            :tag="dataValues.title.data.tag"
                            class="mb-10 text-4xl md:mb-16 md:text-5xl"
                        />
                    </EditableElement>
                </div>

                <!-- Grid -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <!-- Top Row - 2 cards -->
                    <Card
                        field-prefix="topLeftCard"
                        :blockId="block.id"
                        :image="dataValues.topLeftCardImage"
                        :tag="dataValues.topLeftCardTag"
                        :title="dataValues.topLeftCardTitle"
                        :description="dataValues.topLeftCardDescription"
                        :tagTextColor="styleValues.brand"
                        :titleTextColor="styleValues.text"
                        :descriptionTextColor="styleValues.text"
                        :backgroundColor="styleValues.background"
                        :borderColor="styleValues.border"
                        class="rounded-b-lg rounded-t-3xl lg:rounded-lg lg:rounded-tl-3xl"
                    />

                    <Card
                        field-prefix="topRightCard"
                        :blockId="block.id"
                        :image="dataValues.topRightCardImage"
                        :tag="dataValues.topRightCardTag"
                        :title="dataValues.topRightCardTitle"
                        :description="dataValues.topRightCardDescription"
                        :tagTextColor="styleValues.brand"
                        :titleTextColor="styleValues.text"
                        :descriptionTextColor="styleValues.text"
                        :backgroundColor="styleValues.background"
                        :borderColor="styleValues.border"
                        class="rounded-lg lg:rounded-tr-3xl"
                    />
                </div>

                <!-- Bottom Row - 3 cards -->
                <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <Card
                        field-prefix="bottomLeftCard"
                        :blockId="block.id"
                        :image="dataValues.bottomLeftCardImage"
                        :tag="dataValues.bottomLeftCardTag"
                        :title="dataValues.bottomLeftCardTitle"
                        :description="dataValues.bottomLeftCardDescription"
                        :tagTextColor="styleValues.brand"
                        :titleTextColor="styleValues.text"
                        :descriptionTextColor="styleValues.text"
                        :backgroundColor="styleValues.background"
                        :borderColor="styleValues.border"
                        class="rounded-lg lg:rounded-bl-3xl"
                    />

                    <Card
                        field-prefix="bottomCenterCard"
                        :blockId="block.id"
                        :image="dataValues.bottomCenterCardImage"
                        :tag="dataValues.bottomCenterCardTag"
                        :title="dataValues.bottomCenterCardTitle"
                        :description="dataValues.bottomCenterCardDescription"
                        :tagTextColor="styleValues.brand"
                        :titleTextColor="styleValues.text"
                        :descriptionTextColor="styleValues.text"
                        :backgroundColor="styleValues.background"
                        :borderColor="styleValues.border"
                        class="rounded-lg"
                    />

                    <Card
                        field-prefix="bottomRightCard"
                        :blockId="block.id"
                        :image="dataValues.bottomRightCardImage"
                        :tag="dataValues.bottomRightCardTag"
                        :title="dataValues.bottomRightCardTitle"
                        :description="dataValues.bottomRightCardDescription"
                        :tagTextColor="styleValues.brand"
                        :titleTextColor="styleValues.text"
                        :descriptionTextColor="styleValues.text"
                        :backgroundColor="styleValues.background"
                        :borderColor="styleValues.border"
                        class="rounded-t-lg rounded-b-3xl lg:rounded-lg lg:rounded-br-3xl"
                    />
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
